import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { authenticationService, apiJson, fetchWithAuth } from '../../jwt/_services';
import Moment from 'moment';
import Chart from 'react-c3-component';
import 'c3/c3.css';

import chimeSound from '../../assets/sounds/Chime2.mp3';
import logo from '../../assets/images/logo2_x3_text_clear.png';

const Theme = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Saira:wght@500;600;700&display=swap');

    @font-face {
      font-family: 'Myriad Pro';
      src: local('Myriad Pro'), local('MyriadPro-Regular');
      font-weight: 400;
      font-style: normal;
    }

    :root{
      --bg:#050505;
      --panel:#222327;
      --text:#ffffff;
      --muted:#b9c7d6;
      --cyan:#ffffff;
      --amber:#ffcc66;
      --orange:#ff835d;
      --yellow:#ffd336;
    }

    body, .dashboard{background:var(--bg); color:var(--text) !important; font-family:'Myriad Pro','Saira',sans-serif;}

    .card-dash{background:var(--panel); border:0; border-radius:18px; box-shadow:0 6px 24px rgba(0,0,0,.4); overflow:hidden; height:100%;}
    .card-dash .hd{display:flex; justify-content:space-between; align-items:center; font-weight:500; color:#eef5ff; opacity:.9; padding:.7rem 1rem; font-size:.9rem}
    .card-dash .hd small{color:#fff; opacity:.7; font-weight:500}

    .counter-wrap{background:#050505; border:none; border-radius:18px; box-shadow:none; height:100%; display:flex; flex-direction:column; justify-content:center; align-items:center;}
    .brand{letter-spacing:.16em; font-weight:800; font-size:2rem;}
    .brand .evo{color:var(--yellow)}
    .date{color:#fff; font-weight:700; margin-top:0.25rem;}
    .counter-label{letter-spacing:.18em; color:var(--muted); font-size:.72rem; margin-top:1rem;}
    .counter-number{font-family:'Saira',sans-serif; font-weight:400; font-size:14rem; line-height:1; color:var(--cyan); text-shadow:0 0 30px rgba(140,210,255,.35)}
    .btn-outline-amber{border:1px solid #ffd336; color:#ffffff; border-radius:10px; padding:.35rem .8rem; font-weight:700; background:transparent;}

    .waves-pie{display:flex; justify-content:center; align-items:center; flex:1; position:relative; margin-bottom:5px;}
    .pie{width:180px; height:180px; border-radius:50%; position:relative; box-shadow:inset 0 0 0 6px rgba(255,255,255,.08);}
    .pie-label{position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); color:#fff; font-weight:700; font-size:1rem; text-align:center;}

    .waves .bar{height:50px; border-radius:10px; position:relative; overflow:hidden; margin-bottom:10px;}
    .waves .fill{height:100%; border-radius:10px;}
    .waves .label-inside{position:absolute; left:12px; right:12px; top:50%; transform:translateY(-50%); display:flex; justify-content:space-between; font-weight:700;}

    .user-row{background:var(--yellow); color:#222327; border-radius:14px; padding:.25rem .8rem; display:flex; align-items:center; gap:.6rem; box-shadow:inset 0 -2px 0 rgba(0,0,0,.12);}
    .chip{height:28px; width:28px; border-radius:999px; background:#222327; color:var(--yellow); display:inline-flex; align-items:center; justify-content:center; font-weight:900;}

    .avg-card{background:var(--panel); color:#fff; border-radius:18px; height:100%;}
    .avg-time{font-family:'Saira',sans-serif; font-weight:400; color:var(--orange); font-size:5.8rem;}

    .countries .item{display:flex; justify-content:space-between; align-items:center; padding:.95rem 1rem; background:#1a2029; border-radius:12px; margin:.45rem .5rem;}
    .countries .name{font-weight:800; letter-spacing:.04em;}
    .countries .val{color:#a9b5c6; font-weight:700;}

    .wrap{width:100%; max-width:100%; padding:0 2rem;}
    .row.g-4>[class*='col-']{display:flex; flex-direction:column;}
    .row.equal-cols>[class*='col-']{flex:1;}

    .card-dash, .counter-wrap, .avg-card { position: relative; }

    .loading-overlay{
      position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
      background:rgba(0,0,0,.25); z-index:5;
    }
    .loading-overlay-top{
      position:absolute; inset:0; display:flex; align-items:top; justify-content:center;
      background:rgba(0,0,0,.25); z-index:5;
    }
    .spinner{
      width:48px; height:48px; border-radius:50%;
      border:4px solid rgba(255,255,255,.25);
      border-top-color:#ffd336; animation: spin 1s linear infinite;
    }
    .spinner-sm{
      width:30px; height:30px; border-radius:50%;
      border:4px solid rgba(255,255,255,.25);
      border-top-color:#ffd336; animation: spin 1s linear infinite;
    }
    @keyframes spin{ to{ transform: rotate(360deg); } }
  `}</style>
);

const procedures2 = [
  { name: "Decontamination", total: 352 },
  { name: "7mm PBM", total: 295 },
  { name: "25mm PBM", total: 285 },
  { name: "Debridement", total: 195 },
  { name: "Gingivectomy", total: 194 },
  { name: "Throughging", total: 154 },
  { name: "3mm PBM", total: 148 },
  { name: "Hemostasis", total: 120 },
  { name: "Incision / Excision", total: 95 },
  { name: "Frenectomy", total: 85 },
];

const users = [
  { rank: 1, firstName: "Sean Lee", city: "Sarasota, FL" },
  { rank: 2, firstName: "Ana Reyes Retana", city: "Sarasota, FL" },
  { rank: 3, firstName: "Corine Pearson", city: "Sarasota, FL" },
  { rank: 4, firstName: "Aubert Brito", city: "Sarasota, FL" },
  { rank: 5, firstName: "Navi Kaur", city: "Sarasota, FL" },
];

const countries = [
  { country: "UNITED STATES", total: 1548 },
  { country: "BRASIL", total: 385 },
  { country: "MEXICO", total: 150 },
  { country: "COLOMBIA", total: 83 },
  { country: "AUSTRALIA", total: 52 },
];

const wavelengths = [
  { label: "810", value: 35, color: '#d5d5d5' },
  { label: "980", value: 52, color: '#4c96d1' },
  { label: "DUAL", value: 250, color: '#ffd336' },
];

const latestProceduresDemoData = [
  {
    date: "Oct 31st 2025",
    type: "7mm",
    power: "0.3 Watts",
    wavelength: "DUAL",
    treatments: 1,
    dosage: "31 J/cm2",
    length: "39 sec",
    serial: "03756",
    localTime: "1:05:19pm",
  },
  {
    date: "Oct 31st 2025",
    type: "7mm",
    power: "0.3 Watts",
    wavelength: "DUAL",
    treatments: 1,
    dosage: "31 J/cm2",
    length: "39 sec",
    serial: "03756",
    localTime: "1:05:19pm",
  },
  {
    date: "Oct 31st 2025",
    type: "7mm",
    power: "0.3 Watts",
    wavelength: "DUAL",
    treatments: 1,
    dosage: "31 J/cm2",
    length: "39 sec",
    serial: "03756",
    localTime: "1:05:19pm",
  },
];

export default function GeminiEvoDashboard(){

  const [currentUser, setCurrentUser] = useState(authenticationService.currentUserValue);

  //Top Procedures
  const [procedures, setProcedures] = useState([]);
  const [procedureRatio, setProcedureRatio] = useState(100);

  //Total Procedures
  const [totalProcedures, setTotalProcedures] = useState(0);

  //Wavelength Usage
  const [wavelengthUsage, setWavelengthUsage] = useState([]);
  const [wavelengthUsageHasData, setWavelengthUsageHasData] = useState(false);
  const [usage810, setUsage810] = useState(0);
  const [usage980, setUsage980] = useState(0);
  const [usageDual, setUsageDual] = useState(0);

  //Top Users
  const [topUsers, setTopUsers] = useState([]);

  //Average Procedure Time
  const [averageTime, setAverageTime] = useState(0);

  //Top Countries
  const [topCountries, setTopCountries] = useState([]);

  //Latest Procedures
  const [latestProcedures, setLatestProcedures] = useState([]);
  const [procedureTypes, setProcedureTypes] = useState([]);
  const [wavelengthTypes, setWavelengthTypes] = useState(["UNKNOWN", "810", "980", "DUAL"]);
  const [previousProcedure, setPreviousProcedure] = useState(null);

  const [startDate, setStartDate] = useState(Moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(Moment().format('YYYY-MM-DD'));

  const total = wavelengths.reduce((s,w)=>s+w.value,0);
  const max = Math.max(...wavelengths.map(w=>w.value));

  // loading flags
  const [loadingTopProcedures, setLoadingTopProcedures] = useState(true);
  const [loadingWavelengthUsage, setLoadingWavelengthUsage] = useState(true);
  const [loadingTopUsers, setLoadingTopUsers] = useState(true);
  const [loadingAverageTime, setLoadingAverageTime] = useState(true);
  const [loadingTopCountries, setLoadingTopCountries] = useState(true);
  const [loadingLatestProcedures, setloadingLatestProcedures] = useState(true);

  const previousProcedureRef = useRef(null);

  useEffect(() => {
    previousProcedureRef.current = previousProcedure;
  }, [previousProcedure]);

  useEffect(() => {
    const POLL_INTERVAL = 60000;
    const fetchAll = () => {
      console.log('Using latestProcedureRef:', previousProcedureRef.current);
      getTopProceduresData();
      getTotalProceduresData();
      getWavelengthData();
      getTopUsersData();
      getAverageProcedureTimeData();
      getTopCountriesData();
      getProcedureTypesData();
    };
    fetchAll();
    const intervalId = setInterval(fetchAll, POLL_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  // useEffect(() => {
  //   // Define the polling interval in ms (e.g. every 30 seconds)
  //   const POLL_INTERVAL = 60000;
  //
  //   console.log('previousProcedure ->', previousProcedure);
  //
  //   // Function to run all your data fetches
  //   const fetchAll = () => {
  //     getTopProceduresData();
  //     getTotalProceduresData();
  //     getWavelengthData();
  //     getTopUsersData();
  //     getAverageProcedureTimeData();
  //     getTopCountriesData();
  //     getProcedureTypesData();
  //   };
  //
  //   // Run immediately on mount
  //   fetchAll();
  //
  //   // Then poll at interval
  //   const intervalId = setInterval(fetchAll, POLL_INTERVAL);
  //
  //   // Cleanup on unmount to prevent memory leaks
  //   return () => clearInterval(intervalId);
  // }, [previousProcedure]);

  // useEffect(() => {
  //   const delay = (ms) => new Promise((r) => setTimeout(r, ms));
  //
  //   const tasks = [
  //     getTotalProceduresData,
  //     getAverageProcedureTimeData,
  //     getTopProceduresData,
  //     getWavelengthData,
  //     getTopUsersData,
  //     getTopCountriesData,
  //     getProcedureTypesData,
  //   ];
  //
  //   let stopped = false;
  //   let nextCycleTimer;
  //
  //   const runCycle = async () => {
  //     for (const task of tasks) {
  //       if (stopped) return;
  //       await task();          // run the request
  //       //await delay(3000);     // wait 5s before the next one
  //     }
  //     // wait before starting the next cycle (optional)
  //     if (!stopped) nextCycleTimer = setTimeout(runCycle, 60000);
  //   };
  //
  //   runCycle();
  //
  //   return () => {
  //     stopped = true;
  //     clearTimeout(nextCycleTimer);
  //   };
  // }, []);

  // useEffect(() => {
  //
  //   const el = document.querySelector('.c3-tooltip-container');
  //   if (el) el.style.height = '0px';
  //
  //   const delay = (ms) => new Promise((r) => setTimeout(r, ms));
  //
  //   const tasks = [
  //     getTotalProceduresData,
  //     getTopProceduresData,
  //     getWavelengthData,
  //     getAverageProcedureTimeData,
  //     getTopUsersData,
  //     getTopCountriesData,
  //     getProcedureTypesData,
  //   ];
  //
  //   let stopped = false;
  //   let nextCycleTimer;
  //
  //   const runCycle = async () => {
  //     for (let i = 0; i < tasks.length; i += 3) {
  //       if (stopped) return;
  //
  //       // pick two tasks at a time
  //       const pair = tasks.slice(i, i + 3);
  //
  //       // run both concurrently
  //       await Promise.all(pair.map((task) => task()));
  //
  //       // optional delay between pairs
  //       // await delay(3000);
  //     }
  //
  //     // schedule next cycle
  //     if (!stopped) nextCycleTimer = setTimeout(runCycle, 60000);
  //   };
  //
  //   runCycle();
  //
  //   return () => {
  //     stopped = true;
  //     clearTimeout(nextCycleTimer);
  //   };
  // }, []);

  async function getTopProceduresData() {
    try {

      // 1) Don’t call if we already know there’s no user yet
      if (!authenticationService.currentUserValue) {
        // optionally navigate to login or just bail silently until auth finishes
        return;
      }
      setLoadingTopProcedures(true);

      const timezone = Moment.tz.guess();
      const url =
        `https://9xnwyna9id.execute-api.us-west-2.amazonaws.com${process.env.REACT_APP_ENV}` +
        `top-procedures-date-range?MarketType=0&StartDate=${startDate}&EndDate=${endDate}&Timezone=${timezone}&ProductID=1`;

      console.log("getTopProceduresData url: " + JSON.stringify(url));

      // IMPORTANT: Match your old format => ID token + raw header (no "Bearer ")
      const response = await fetchWithAuth(
        url,
        { method: 'GET' },
        {
          tokenType: 'id',
          authScheme: 'raw',
          timeoutMs: 28000,
          maxRetries: 2,
        }
      );

      if (!response.ok) {
        // Handle 401 here (e.g., show toast, navigate once), not by reloading
        if (response.status === 401) {
          console.warn('Unauthorized after refresh. Consider logging out here once.');
          // authenticationService.logout(); // if you do this, ensure it doesn’t force a full page reload
        }
        const text = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
      }

      const body = await response.json();
      console.log('getTopProceduresData response:', body);

      if (body.status === 'Success' && Array.isArray(body.results)) {
        const largest = Math.max(0, ...body.results.map(r => r.total ?? 0));
        setProcedures(body.results);
        setProcedureRatio(2 * largest);
      } else {
        setProcedures([]);
        setProcedureRatio(0);
      }
    } catch (err) {
      console.error('getTopProceduresData error:', err);
    } finally {
      setLoadingTopProcedures(false);
    }
  }

  async function getTotalProceduresData() {
    try {
      const timezone = Moment.tz.guess();

      const url =
        `https://9xnwyna9id.execute-api.us-west-2.amazonaws.com${process.env.REACT_APP_ENV}` +
        `total-procedures-date-range?MarketType=0&StartDate=${startDate}&EndDate=${endDate}&Timezone=${timezone}&ProductID=1`;

      console.log('getTotalProceduresData url:', url);

      // Match your backend: use ID token, raw (no "Bearer ")
      const res = await fetchWithAuth(
        url,
        { method: 'GET' },
        { tokenType: 'id', authScheme: 'raw', timeoutMs: 28000, maxRetries: 2 }
      );

      // If not authenticated yet, skip (let PrivateRoute/auth flow handle redirect)
      if (res.status === 401) {
        console.warn('Unauthenticated; skipping total procedures load.');
        return;
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${res.statusText}: ${text || '(empty body)'}`);
      }

      const body = await res.json();
      console.log('getTotalProceduresData response:', body);

      if (body.status === 'Success' && Array.isArray(body.results) && body.results.length > 0) {
        setTotalProcedures(body.results[0].total ?? 0);
      } else {
        setTotalProcedures(0);
      }
    } catch (err) {
      console.error('getTotalProceduresData error:', err);
    } finally {
      // setLoadingTotalProcedures(false);
    }
  }

  async function getWavelengthData() {
    try {
      setLoadingWavelengthUsage(true);

      const timezone = Moment.tz.guess();
      const url =
        `https://9xnwyna9id.execute-api.us-west-2.amazonaws.com${process.env.REACT_APP_ENV}` +
        `wavelength-usage?MarketType=0&FetchType=2&StartDate=${startDate}&EndDate=${endDate}&Timezone=${timezone}&ProductID=1`;

      console.log('getWavelengthData url:', url);

      // use ID token with raw header format (same as your API expects)
      const res = await fetchWithAuth(
        url,
        { method: 'GET' },
        { tokenType: 'id', authScheme: 'raw', timeoutMs: 28000, maxRetries: 2 }
      );

      if (res.status === 401) {
        console.warn('Unauthenticated; skipping wavelength usage load.');
        return;
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
      }

      const body = await res.json();
      console.log('getWavelengthData response:', body);

      if (body.status === 'Success' && Array.isArray(body.results) && body.results.length > 0) {
        const data = body.results[0];
        setWavelengthUsage(data);
        setWavelengthUsageHasData(true);

        // --- Format usage times ---
        const formatDuration = (seconds) => {
          const d = Number(seconds) || 0;
          const h = Math.floor(d / 3600);
          const m = Math.floor((d % 3600) / 60);
          const hDisplay = h > 0 ? `${h} ${h === 1 ? 'hr' : 'hrs'}, ` : '';
          const mDisplay = m > 0 ? `${m} ${m === 1 ? 'min' : 'mins'} ` : '';
          return hDisplay + mDisplay;
        };

        setUsage810(formatDuration(data.m810));
        setUsage980(formatDuration(data.m980));
        setUsageDual(formatDuration(data.mDual));
      } else {
        // handle empty results
        //setWavelengthUsageHasData(false);
        // setWavelengthUsage(null);
        // setUsage810('');
        // setUsage980('');
        // setUsageDual('');
      }
    } catch (err) {
      console.error('getWavelengthData error:', err);
    } finally {
      setLoadingWavelengthUsage(false);
    }
  }

  async function getTopUsersData() {
    try {
      setLoadingTopUsers(true);

      const timezone = Moment.tz.guess();
      const url =
        `https://9xnwyna9id.execute-api.us-west-2.amazonaws.com${process.env.REACT_APP_ENV}` +
        `get-top-performers?Timezone=${timezone}`;

      console.log('getTopUsersData url:', url);

      // Use ID token with raw header (same as your other endpoints)
      const res = await fetchWithAuth(
        url,
        { method: 'GET' },
        { tokenType: 'id', authScheme: 'raw', timeoutMs: 28000, maxRetries: 2 }
      );

      // Handle auth or network issues gracefully
      if (res.status === 401) {
        console.warn('[getTopUsersData] Unauthenticated — redirect handled elsewhere');
        return;
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.warn(
          `[getTopUsersData] HTTP ${res.status} ${res.statusText}: ${text || '(empty body)'}`
        );
        return;
      }

      const body = await res.json();
      console.log('getTopUsersData response:', body);

      if (body.status === 'Success' && Array.isArray(body.response) && body.response.length > 0) {
        setTopUsers(body.response);
      } else {
        setTopUsers([]);
      }
    } catch (err) {
      console.error('getTopUsersData error:', err);
    } finally {
      setLoadingTopUsers(false);
    }
  }

  async function getAverageProcedureTimeData() {
    try {
      setLoadingAverageTime(true);

      const timezone = Moment.tz.guess();
      const url =
        `https://9xnwyna9id.execute-api.us-west-2.amazonaws.com${process.env.REACT_APP_ENV}` +
        `total-procedures-date-range?FetchType=1&MarketType=0&StartDate=${startDate}&EndDate=${endDate}&Timezone=${timezone}&ProductID=1`;

      console.log('getAverageProcedureTimeData url:', url);

      // use ID token, raw Authorization header (like other endpoints)
      const res = await fetchWithAuth(
        url,
        { method: 'GET' },
        { tokenType: 'id', authScheme: 'raw', timeoutMs: 28000, maxRetries: 2 }
      );

      // handle unauthenticated case gracefully
      if (res.status === 401) {
        console.warn('[getAverageProcedureTimeData] Unauthenticated; redirect handled elsewhere.');
        return;
      }

      // handle 5xx/timeout gracefully
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.warn(
          `[getAverageProcedureTimeData] HTTP ${res.status} ${res.statusText}: ${text || '(empty body)'}`
        );
        return;
      }

      const body = await res.json();
      console.log('getAverageProcedureTimeData response:', body);

      if (body.status === 'Success' && Array.isArray(body.results) && body.results.length > 0) {
        const avgSeconds = Number(body.results[0].avg_total_usage) || 0;

        const h = Math.floor(avgSeconds / 3600);
        const m = Math.floor((avgSeconds % 3600) / 60);
        const s = Math.floor(avgSeconds % 60);

        // Format to h:mm:ss or m:ss
        const pad = (n) => (n < 10 ? `0${n}` : n);
        const timeDisplay = h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;

        setAverageTime(timeDisplay);
      } else {
        // No results
        setAverageTime('0:00');
      }
    } catch (err) {
      console.error('getAverageProcedureTimeData error:', err);
      setAverageTime('0:00');
    } finally {
      setLoadingAverageTime(false);
    }
  }

  async function getTopCountriesData() {
    try {
      setLoadingTopCountries(true);

      const timezone = Moment.tz.guess();
      const url =
        `https://9xnwyna9id.execute-api.us-west-2.amazonaws.com${process.env.REACT_APP_ENV}` +
        `total-procedures-date-range?FetchType=2&MarketType=0&StartDate=${startDate}&EndDate=${endDate}&Timezone=${timezone}&ProductID=1`;

      console.log('getTopCountriesData url:', url);

      // ✅ Use fetchWithAuth — handles token, refresh, retries automatically
      const res = await fetchWithAuth(
        url,
        { method: 'GET' },
        { tokenType: 'id', authScheme: 'raw', timeoutMs: 28000, maxRetries: 2 }
      );

      // Handle unauthenticated case gracefully
      if (res.status === 401) {
        console.warn('[getTopCountriesData] Unauthenticated; redirect handled elsewhere.');
        return;
      }

      // Handle server / network issues gracefully
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.warn(
          `[getTopCountriesData] HTTP ${res.status} ${res.statusText}: ${text || '(empty body)'}`
        );
        return;
      }

      const body = await res.json();
      console.log('getTopCountriesData response:', body);

      // ✅ Process response
      if (body.status === 'Success' && Array.isArray(body.results) && body.results.length > 0) {
        setTopCountries(body.results);
      } else {
        setTopCountries([]);
      }
    } catch (err) {
      console.error('getTopCountriesData error:', err);
      setTopCountries([]);
    } finally {
      setLoadingTopCountries(false);
    }
  }

  async function getProcedureTypesData() {
    try {
      setloadingLatestProcedures(true);

      const url =
        `https://9xnwyna9id.execute-api.us-west-2.amazonaws.com${process.env.REACT_APP_ENV}` +
        `all-procedures`;

      console.log('getProcedureTypesData url:', url);

      // ✅ Use fetchWithAuth – handles token, refresh, retries automatically
      const res = await fetchWithAuth(
        url,
        { method: 'GET' },
        { tokenType: 'id', authScheme: 'raw', timeoutMs: 28000, maxRetries: 2 }
      );

      // Handle unauthenticated user gracefully
      if (res.status === 401) {
        console.warn('[getProcedureTypesData] Unauthenticated; redirect handled elsewhere.');
        return;
      }

      // Handle server/network failures cleanly
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.warn(
          `[getProcedureTypesData] HTTP ${res.status} ${res.statusText}: ${text || '(empty body)'}`
        );
        return;
      }

      // Parse and log body
      const body = await res.json();
      console.log('getProcedureTypesData response:', body);

      if (body.status === 'Success' && Array.isArray(body.results) && body.results.length > 0) {
        setProcedureTypes(body.results);
        // Optionally trigger dependent load
        getLatestProcedureData();
      } else {
        setProcedureTypes([]);
      }
    } catch (err) {
      console.error('getProcedureTypesData error:', err);
      setProcedureTypes([]);
    } finally {
      setloadingLatestProcedures(false);
    }
  }

  async function getLatestProcedureData() {
    try {
      setloadingLatestProcedures(true);

      const timezone = Moment.tz.guess();
      const url =
        `https://9xnwyna9id.execute-api.us-west-2.amazonaws.com${process.env.REACT_APP_ENV}` +
        `get-all-preformed-procedures-for-date-range?StartDate=${startDate}&EndDate=${endDate}&Timezone=${timezone}`;

      console.log('getLatestProcedureData url:', url);

      // ✅ Use fetchWithAuth for token management and retries
      const res = await fetchWithAuth(
        url,
        { method: 'GET' },
        { tokenType: 'id', authScheme: 'raw', timeoutMs: 28000, maxRetries: 2 }
      );

      // Handle unauthenticated users gracefully
      if (res.status === 401) {
        console.warn('[getLatestProcedureData] Unauthenticated; redirect handled elsewhere.');
        return;
      }

      // Handle server/network issues
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.warn(
          `[getLatestProcedureData] HTTP ${res.status} ${res.statusText}: ${text || '(empty body)'}`
        );
        return;
      }

      const body = await res.json();
      console.log('getLatestProcedureData response:', body);

      if (body.status === 'Success' && Array.isArray(body.results) && body.results.length > 0) {
        // ✅ Update UI data
        setLatestProcedures(body.results);

        console.log('previous procedure: ', JSON.stringify(previousProcedure));
        console.log('latest procedure: ', JSON.stringify(body.results[0]));

        // 🔔 Compare and chime only if the latest record changed
        if (!previousProcedure || JSON.stringify(previousProcedure) !== JSON.stringify(body.results[0])) {
          console.log('Objects are not equal — updating previousProcedure');
          setPreviousProcedure(body.results[0]);
          playChime();
        }
      } else {
        setLatestProcedures([]);
      }
    } catch (err) {
      console.error('getLatestProcedureData error:', err);
      setLatestProcedures([]);
    } finally {
      setloadingLatestProcedures(false);
    }
  }

  // const playChime = () => {
  //   audioRef.current.currentTime = 0; // rewind if already playing
  //   audioRef.current.play();
  // };

  const playChime = () => {
    const sound = new Audio(chimeSound);
    sound.play();
  };

  let m810 = parseFloat(wavelengthUsage.m810);
  let m980 = parseFloat(wavelengthUsage.m980);
  let mDual = parseFloat(wavelengthUsage.mDual);
  //console.log("m810: " + m810)
  //let usage = this.state.totalUsage;
  //console.log("usage: " + usage)
  const options = {
    data: {
      columns: [
        ['810', m810],
        ['980', m980],
        ['Dual', mDual],
      ],

      type: 'pie',
      tooltip: {
        show: false
      }
    },
    pie: {
      label: {
        show: true
      },
    },
    legend: {
      hide: true
    },
    color: {
      pattern: ['#d5d5d5', '#4c96d1', '#ffd336']
    }
  }

  const Th = ({ children }) => (
    <th className="px-3 py-3 text-sm font-medium uppercase tracking-wide">{children}</th>
  );
  const Td = ({ children }) => (
    <td className="px-3 py-4 align-middle">{children}</td>
  );

  const lpData = latestProcedures.map((procedure, key) => {

    var procedureLabel = "Manual";
    var dosageLabel = "";

    if (procedure.power) {
      procedureLabel = "Manual";
    } else if (procedure.preset) {
      let procedureArray = procedureTypes.find( t => t.OBJID == procedure.preset );
      //console.log("procedureArray: " + JSON.stringify(procedureArray));
      if (procedureArray) {
        procedureLabel = procedureTypes.find( t => t.OBJID == procedure.preset ).Name;

        if (procedureLabel == "25mm") {
          dosageLabel = Math.round(
            (1 * procedure.totalseconds) / 4.91
          ) + " J/cm2";
        } else if (procedureLabel == "7mm") {
          dosageLabel = Math.round(
            (0.3 * procedure.totalseconds) / 0.38
          ) + " J/cm2";
        } else if (procedureLabel == "3mm") {
          dosageLabel = Math.round(
            (0.3 * procedure.totalseconds) / 0.07
          ) + " J/cm2";
        }

      } else {
        procedureLabel = "Unknown"
      }
    }

    var powerLabel = "0.0 Watts";

    if (procedure.watts) {
      powerLabel = (procedure.watts/10).toFixed(1)+" Watts";
    } else if (procedure.power) {
      powerLabel = (procedure.power/10).toFixed(1)+" Watts";
    }

    var wavelengthType = wavelengthTypes[procedure.wavelength];

    var d = Number(procedure.totalseconds);
    var h = Math.floor(d / 3600);
    var m = Math.floor(d % 3600 / 60);
    var s = Math.floor(d % 3600 % 60);

    var hDisplay = h > 0 ? h + (h == 1 ? " hr, " : " hrs, ") : "";
    var mDisplay = m > 0 ? m + (m == 1 ? " min, " : " mins, ") : "";
    var sDisplay = s > 0 ? s + (s == 1 ? " sec" : " sec") : "";

    return {
      id: key,
      serialNumber: procedure.SerialNumber,
      date: procedure.timestamp,
      procedureType: procedureLabel,
      wavelength: wavelengthType,
      treatments: procedure.treatments,
      dosage: dosageLabel,
      power: powerLabel,
      length: hDisplay + mDisplay + sDisplay,
      totalSeconds: procedure.totalseconds,
      time: Moment(procedure.timestamp).format("h:mm:ssa")
    };
  });

  // Row styling logic (similar to getTrProps)
  const getRowStyle = (row) => {
    if (!row) return {};
    if (
      row.procedureType !== "7mm" &&
      row.procedureType !== "3mm" &&
      row.procedureType !== "25mm"
    ) {
      if (row.totalSeconds < 11) {
      return { color: "#FC9E2D" };
      }
    }
    return {};
  };

  return (
    <div className="dashboard container-fluid py-4">
      <Theme />
      <div className="wrap">
        {/* Row 1 */}
        <div className="row g-4 equal-cols align-items-stretch">
          <div className="col-lg-4">
            <div className="card-dash h-100">
              {procedures.length==0 && loadingTopProcedures && (
                <div className="loading-overlay"><div className="spinner" /></div>
              )}
              <div style={{ "font-size":"24px", color: "#fff" }} className="hd"><span>Top 10 Procedures</span><span>Today</span></div>
              <div className="pb-2">
                {procedures.map((p,i)=> (
                  <div style={{ borderBottom: "1px solid #ffd336" }} key={i} className="d-flex justify-content-between px-3 py-1">
                    <span style={{ fontSize: 21, color: "#7c7c7c" }}>{p.name}</span>
                    <span style={{ fontSize: 21 }}>{p.total}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="counter-wrap h-100 text-center">
              <div className="brand pt-1"><img style={{ width: "55%" }} src={logo} alt="GEMINI EVO"/></div>
              <div style={{ fontSize: 25, fontWeight: 300 }} className="date mb-3 mt-4">{new Date().toLocaleDateString()}</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#ffffff" }} className="counter-label">Live Procedure Count</div>
              <div className="counter-number">{totalProcedures}</div>
              <button style={{ width: 440, height: 60, fontSize: 20, fontWeight: 500, color: "#fff" }} className="btn btn-outline-amber mt-2">Total Procedures Today</button>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card-dash h-100">
              {!wavelengthUsageHasData && (
                <div className="loading-overlay"><div className="spinner" /></div>
              )}
              <div style={{ "font-size":"24px", color: "#fff" }} className="hd"><span>Wavelength Usage</span><span>Today</span></div>
              {wavelengthUsageHasData && (
                <div className="pb-3 pl-3 pr-3 d-flex flex-column justify-content-between">
                  <div className="waves-pie">
                      <div id="waveChart">
                          <Chart
                            id="waveChart"
                            style={{ height: '250px', width: '100%' }}
                            config={options}
                          />
                      </div>
                    {
                      // <div
                      //   className="pie"
                      //   style={{
                      //     background: `conic-gradient(${wavelengths
                      //       .map((w, i) => {
                      //         const start = (wavelengths.slice(0, i).reduce((s, x) => s + x.value, 0) / total) * 100;
                      //         const end = ((wavelengths.slice(0, i + 1).reduce((s, x) => s + x.value, 0)) / total) * 100;
                      //         return `${w.color} ${start}% ${end}%`;
                      //       })
                      //       .join(', ')})`
                      //   }}
                      // >
                      //   <div className="pie-label"></div>
                      // </div>
                    }
                  </div>

                  <div className="waves">
                    <div key="810" className="bar" style={{background:"#d5d5d5"}}>
                      <div style={{ fontWeight: "400", color: "#050505", display: "flex", justifyContent: "space-between", alignItems: "center", height: "100%" }} className="label-inside"><span style={{ "font-size":"30px" }}>810</span><span style={{ "font-size":"40px" }}>{usage810}</span></div>
                    </div>
                    <div key="980" className="bar" style={{background:"#4c96d1"}}>
                      <div style={{ fontWeight: "400", color: "#050505", display: "flex", justifyContent: "space-between", alignItems: "center", height: "100%" }} className="label-inside"><span style={{ "font-size":"30px" }}>980</span><span style={{ "font-size":"40px" }}>{usage980}</span></div>
                    </div>
                    <div key="Dual" className="bar" style={{background:"#ffd336"}}>
                      <div style={{ fontWeight: "400", color: "#050505", display: "flex", justifyContent: "space-between", alignItems: "center", height: "100%" }} className="label-inside"><span style={{ "font-size":"30px" }}>DUAL</span><span style={{ "font-size":"40px" }}>{usageDual}</span></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 2 */}
        <div className="row g-4 equal-cols align-items-stretch mt-3" style={{ display: 'flex', alignItems: 'flex-start' }}>
          <div className="col-lg-4">
            <div className="card-dash h-100">
              {topUsers.length==0 && loadingTopUsers && (
                <div className="loading-overlay"><div className="spinner" /></div>
              )}
              <div style={{ "font-size":"24px", color: "#fff" }} className="hd"><span>Top 5 Users</span><span>Today</span></div>
              <div className="pr-3 pl-3 pt-1 pb-3">
                {topUsers.slice(0, 5).map((u, i) => (
                  <div key={i+1} className="user-row mb-2">
                    <span className="chip">{i+1}</span>
                    <div>
                      <div style={{ fontSize:"20px", color: "#222327", fontWeight: 700 }} className="fw-bold">{u.firstName + " "+u.lastName}</div>
                      <div style={{fontSize:'.85rem', color: "#222327"}}>{u.city + ", "+u.state}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-lg-4" style={{ marginTop: 'auto' }}>
            <div className="avg-card h-100 d-flex flex-column justify-content-center text-center  py-5">
              <div style={{ "font-size":"24px", color: "#fff" }} className="fw-bold">Average Procedure Time</div>
              <div style={{ color: "#ffd336", height: 115 }} className="avg-time">{averageTime}</div>
              <div style={{ fontSize: 22 }} className="text-white-100">minutes</div>
              <div className="mt-3 ml-5 mr-5" style={{background:'#050505',color:'#cbd6e6',borderRadius:'10px',padding:'.4rem .9rem',display:'inline-block', height: 50, display: "flex", justifyContent: "center", alignItems: "center", fontSize: 18, color: "#fff"}}>Based on {totalProcedures} procedures today</div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card-dash h-100">
              {topCountries.length==0 && loadingTopCountries && (
                <div className="loading-overlay"><div className="spinner" /></div>
              )}
              <div style={{ "font-size":"24px", color: "#fff" }} className="hd"><span>Top 5 Countries</span><span>Today</span></div>
              <div className="pr-3 pl-3 pt-1 pb-3">
                {topCountries.map((c,i)=> (
                  <div key={i} className="d-flex justify-content-between align-items-center mb-2 py-1 px-3" style={{background:'#050505', borderRadius: 8 }}>
                    <span style={{ fontSize:22 }} className="fw-bold">{c.country}</span>
                    <span style={{ fontSize:34, color: "#7c7c7c" }}>{c.total.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="row g-4 equal-cols align-items-stretch mt-3" style={{ display: 'flex', alignItems: 'flex-start' }}>
          <div className="col-lg-12">
            <div className="card-dash h-100">
              {latestProcedures.length==0 && loadingLatestProcedures && (
                <div className="loading-overlay-top pt-4"><div className="spinner-sm" /></div>
              )}
              <div style={{ "font-size":"24px", color: "#fff" }} className="hd"><span>Live Procedure Reports</span><span>Today</span></div>
              <div className="pr-3 pl-3 pt-1 pb-3">
                <div className="overflow-x-auto">
                  <table style={{ fontSize: 20 }} className="w-100 table-auto">
                    <thead>
                      <tr className="text-center text-[#9aa3af] text-md">
                      <th className="px-3 py-2">Date of Procedure</th>
                      <th className="px-3 py-2">Procedure Type</th>
                      <th className="px-3 py-2">Power</th>
                      <th className="px-3 py-2">Wavelength</th>
                      <th className="px-3 py-2">Treatments</th>
                      <th className="px-3 py-2">Dosage</th>
                      <th className="px-3 py-2">Procedure Length</th>
                      <th className="px-3 py-2">Serial Number</th>
                      <th className="px-3 py-2">Local Time</th>
                      </tr>
                    </thead>
                    <tbody>
                    {lpData.slice(0, 3).map((r, i) => (
                      <tr
                      key={i}
                      className="text-center border-t border-white/10 text-[15px] text-white/90 h-[30px]"
                      style={{ ...getRowStyle(r), lineHeight: "30px" }}
                      >
                        <td className="px-3 py-1">{Moment(r.date).format("MMM Do YYYY")}</td>
                        <td className="px-3 py-1">{r.procedureType}</td>
                        <td className="px-3 py-1">{r.power}</td>
                        <td className="px-3 py-1">{r.wavelength}</td>
                        <td className="px-3 py-1">{r.treatments}</td>
                        <td className="px-3 py-1">{r.dosage}</td>
                        <td className="px-3 py-1">{r.length}</td>
                        <td className="px-3 py-1">{r.serialNumber}</td>
                        <td className="px-3 py-1">{r.time}</td>
                      </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
